/**
 * ERPinnedFilters — collapsible "Pinned Filters" section in the left
 * sidebar (Sprint 4 Commit 3, relay `gvVCRKtvjiuK7VqlXXql`,
 * EFISSUE-808).
 *
 * Each pin is a clickable chip showing the tag's color dot, name, and
 * an unread count badge. Hover reveals a × that calls `onUnpin`. Click
 * the chip body to toggle that tag in the stream's `activeFilters`
 * set (same handler as the inline tag pills + the filter bar).
 *
 * The pin-add row at the bottom: a small input that prefix-matches
 * against `KNOWN_TAGS`, lets the user pick one to pin. No suggestion
 * dropdown UI in V1 — just a small list rendered below the input
 * when the query has matches.
 *
 * Storage + unread-count tracking live in App.tsx. This component
 * is presentation only.
 */

import { useState } from "react";
import { tagPillStyle, KNOWN_TAGS } from "../lib/tagColors";
import { Caret } from "./Caret";

interface Props {
  pinned: string[];
  unreadCounts: Record<string, number>;
  activeFilters: string[];
  /**
   * Universe of tags the pin-a-tag input can match against. Parent
   * composes this from `KNOWN_TAGS` plus the set of tags actually
   * present on visible messages, so any tag someone used in a post
   * becomes pinnable — not just pre-colored ones (bug fix per planner
   * `o1X8n7mnQCoh4qGzcH0p`).
   */
  searchableTags: string[];
  onTogglePin: (tag: string) => void;
  onPinTag: (tag: string) => void;
  onUnpinTag: (tag: string) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

const KNOWN_TAG_SET = new Set<string>(KNOWN_TAGS);

/**
 * Dot color for a pinned tag. Uses the colored tagPillStyle when the
 * tag is in KNOWN_TAGS; otherwise falls back to `--er-fg-4` (muted
 * gray) so uncolored tags still show a stable visual anchor.
 */
function pinDotColor(tag: string): string {
  if (KNOWN_TAG_SET.has(tag)) return tagPillStyle(tag).color;
  return "var(--er-fg-4)";
}

export function ERPinnedFilters({
  pinned,
  unreadCounts,
  activeFilters,
  searchableTags,
  onTogglePin,
  onPinTag,
  onUnpinTag,
  collapsed,
  onToggleCollapsed,
}: Props) {
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);

  const matches = query.trim().length === 0
    ? []
    : searchableTags
        .filter(t => t.toLowerCase().startsWith(query.trim().toLowerCase()))
        .filter(t => !pinned.includes(t));

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        className="sb-summary"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
      >
        <Caret open={!collapsed} />
        Filters
        {pinned.length > 0 && (
          <span style={{ marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 10 }}>
            {pinned.length}
          </span>
        )}
      </button>

      {!collapsed && (
        <div className="sb-collapsible-body" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {pinned.length === 0 && !adding && (
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                fontStyle: "italic",
                padding: "2px 6px 4px",
              }}
            >
              No pinned filters yet.
            </div>
          )}
          {pinned.map(tag => {
            const isActive = activeFilters.includes(tag);
            const count = unreadCounts[tag] ?? 0;
            return (
              <PinRow
                key={tag}
                tag={tag}
                color={pinDotColor(tag)}
                isActive={isActive}
                unread={count}
                onClick={() => onTogglePin(tag)}
                onUnpin={() => onUnpinTag(tag)}
              />
            );
          })}
          {adding ? (
            <div style={{ padding: "2px 0", display: "flex", flexDirection: "column", gap: 4 }}>
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="tag name…"
                onBlur={() => {
                  // Small delay so a click in the matches list registers
                  // before the input loses focus.
                  setTimeout(() => {
                    if (!document.activeElement?.closest("[data-er-pin-match]")) {
                      setAdding(false);
                      setQuery("");
                    }
                  }, 120);
                }}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    setAdding(false);
                    setQuery("");
                  } else if (e.key === "Enter" && matches.length > 0) {
                    onPinTag(matches[0]);
                    setAdding(false);
                    setQuery("");
                  }
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  fontSize: 12,
                  padding: "4px 6px",
                  borderRadius: 4,
                  border: "1px solid var(--border)",
                  background: "var(--bg)",
                  color: "var(--text)",
                  fontFamily: "var(--font-mono)",
                }}
              />
              {matches.length > 0 && (
                <div
                  data-er-pin-match
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    paddingTop: 2,
                  }}
                >
                  {matches.slice(0, 6).map(t => {
                    const style = tagPillStyle(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          onPinTag(t);
                          setAdding(false);
                          setQuery("");
                        }}
                        title={KNOWN_TAG_SET.has(t) ? t : `${t} (no color defined)`}
                        style={{
                          fontSize: 10,
                          padding: "1px 6px",
                          borderRadius: 3,
                          background: style.bg,
                          color: style.color,
                          border: style.border,
                          fontFamily: "var(--font-mono)",
                          cursor: "pointer",
                        }}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="sb-pin-filter"
              onClick={() => setAdding(true)}
            >
              + pin a filter…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PinRow({
  tag,
  isActive,
  onClick,
  onUnpin,
}: {
  tag: string;
  color: string;
  isActive: boolean;
  unread: number;
  onClick: () => void;
  onUnpin: () => void;
}) {
  return (
    <div
      className={`filter-chip${isActive ? " active" : ""}`}
      onClick={onClick}
    >
      <span className="hash">#</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {tag}
      </span>
      <span
        className="x"
        onClick={e => { e.stopPropagation(); onUnpin(); }}
        aria-label={`Unpin ${tag}`}
        title={`Unpin ${tag}`}
        role="button"
      >×</span>
    </div>
  );
}
